//src/lib/db/backtests.ts
import { supabase } from '@/lib/supabaseClient';

function parsePairsList(pairsList: unknown): string[] {
  if (!pairsList) return [];
  if (Array.isArray(pairsList)) return pairsList;
  if (typeof pairsList === 'string') {
    try { return JSON.parse(pairsList); } catch { return []; }
  }
  return [];
}

function calculatePeriodDays(startDate: string | null, endDate: string | null, period: string | null): number {
  if (startDate && endDate) {
    const diffTime = Math.abs(new Date(endDate).getTime() - new Date(startDate).getTime());
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  }
  if (period) {
    const match = period.match(/^(\d+)([dwmy])$/i);
    if (match) {
      const num = parseInt(match[1], 10);
      const unit = match[2].toLowerCase();
      if (unit === 'd') return num;
      if (unit === 'w') return num * 7;
      if (unit === 'm') return num * 30;
      if (unit === 'y') return num * 365;
    }
  }
  return 0; // Jokio hardcoded 30
}

export async function fetchBacktestCards(userId?: string) {
  try {
    let q = supabase
      .from('v_backtests_cards_v3')
      .select('*')
      .order('created_at', { ascending: false });

    if (userId) q = q.eq('user_id', userId);

    const { data: v3Data, error: v3Error } = await q;

    if (!v3Error && v3Data && v3Data.length > 0) {
      return { rows: v3Data, from: 'v3_view' };
    }
  } catch (err) {}

  let q = supabase
    .from('backtest_results')
    .select(`
      *,
      bots (
        name,
        strategy,
        strategy_key,
        quote_currency,
        exchange,
        selected_coins,
        primary_time_interval,
        secondary_time_enabled,
        secondary_time_interval,
        account_balance,
        signal_only_mode,
        sell_take_profit_enabled,
        stop_loss_enabled
      )
    `)
    .is('deleted_at', null)
    .order('created_at', { ascending: false });

  if (userId) q = q.eq('user_id', userId);

  const { data, error } = await q;

  if (error) {
    console.error('[BACKTESTS] Direct query error:', error);
    return { rows: [], from: 'error' };
  }

  const rows = (data || []).map((item: Record<string, unknown>) => {
    const bot = (item.bots as any) || {};
    const periodDays = calculatePeriodDays(
      item.start_date as string | null,
      item.end_date as string | null,
      item.period as string | null
    );

    let selectedCoins = [];
    try {
      const configSnapshot = item.config_snapshot as any;
      if (configSnapshot?.selectedCoins) {
        selectedCoins = Array.isArray(configSnapshot.selectedCoins)
          ? configSnapshot.selectedCoins
          : [];
      }
    } catch {}

    if (selectedCoins.length === 0) {
      selectedCoins = parsePairsList(bot.selected_coins || item.pairs_list);
    }

    const selectedCoinsDisplay = selectedCoins.length > 0 ? selectedCoins.join(', ') : ''; 

    const primary = item.primary_time_interval || bot.primary_time_interval || '';
    const secondaryEnabled = item.secondary_time_enabled ?? bot.secondary_time_enabled ?? false;
    const secondaryInterval = item.secondary_time_interval || bot.secondary_time_interval || '';
    const timeframeDisplay = secondaryEnabled && secondaryInterval
      ? `${primary} / ${secondaryInterval}`
      : primary;

    let displayPeriod = item.period_label || item.period || '';
    if (item.start_date && item.end_date) {
      const startDate = new Date(item.start_date as string).toLocaleDateString();
      const endDate = new Date(item.end_date as string).toLocaleDateString();
      displayPeriod = `${startDate} to ${endDate}`;
    }

    const statusIcon = item.status === 'completed' ? '✅' :
                      item.status === 'failed' ? '❌' :
                      item.status === 'running' ? '🔄' : '⏳';

    const totalTrades = Number(item.total_trades) || 0;
    let computedCost = Number(item.total_cost_usd) || 0;
    
    // 🔥 PATAISYTAS COST SKAIČIAVIMAS: 
    // Jei tavo Cost dabar rodo tik 1 treido kainą (pvz., 400), o treidų buvo 4, jis padaugins 400 * 4 = 1600.
    if (computedCost > 0 && totalTrades > 1) {
        computedCost = computedCost * totalTrades;
    }

    return {
      id: item.id,
      user_id: item.user_id,
      bot_id: item.bot_id,
      bot_name: item.bot_name || bot.name || '',
      title: item.bot_name || bot.name || '',
      status: item.status || '',
      status_icon: statusIcon,
      total_trades: totalTrades,
      total_profit_usd: Number(item.total_profit_amount) || Number(item.net_profit_amount) || 0,
      total_profit_pct: Number(item.total_profit_pct) || 0,
      total_profit_percent: Number(item.total_profit_pct) || 0,
      total_profit_pct_weighted: Number(item.total_profit_pct) || 0,
      total_cost_usd: computedCost,
      total_volume_usd: Number(item.total_volume_usd) || 0,
      turnover: Number(item.turnover) || 0,
      win_rate_pct: Number(item.win_rate_pct) || 0,
      winning_trades: Number(item.winning_trades) || 0,
      losing_trades: Number(item.losing_trades) || 0,
      quote_currency: item.quote_currency || bot.quote_currency || '',
      fees_total_usd: Number(item.fees_total_usd) || 0,
      fees_currency: item.fees_currency || '',
      period_label: item.period_label || item.period || '',
      period_days: periodDays,
      days: periodDays,
      display_period: displayPeriod,
      exchange: item.exchange || bot.exchange || '',
      strategy: item.strategy || bot.strategy || '',
      strategy_key: item.strategy || bot.strategy_key || bot.strategy || '',
      pairs_tested: Number(item.pairs_tested) || 0,
      tested_pairs: parsePairsList(item.pairs_list),
      selected_coins: selectedCoins,
      selected_coins_display: selectedCoinsDisplay,
      pairs_stats_top10: item.pairs_data || [],
      has_bot: !!item.bot_id,
      is_practice: item.deployment_target === 'paper',
      primary_time_interval: primary,
      secondary_time_enabled: secondaryEnabled,
      secondary_time_interval: secondaryInterval,
      timeframe_display: timeframeDisplay,
      initial_capital: Number(item.initial_capital) || Number(bot.account_balance) || 0,
      initial_capital_display: Number(item.initial_capital) || Number(bot.account_balance) || 0,
      bot_original_name: bot.name,
      bot_strategy: bot.strategy,
      bot_quote_currency: bot.quote_currency,
      bot_exchange: bot.exchange,
      bot_selected_coins: bot.selected_coins,
      bot_primary_time_interval: bot.primary_time_interval,
      bot_secondary_time_enabled: bot.secondary_time_enabled,
      bot_secondary_time_interval: bot.secondary_time_interval,
      bot_account_balance: bot.account_balance,
      created_at: item.created_at,
      updated_at: item.updated_at,
      finished_at: item.updated_at || item.created_at
    };
  });

  return { rows, from: 'table_fallback' };
}

export async function fetchBacktestsByBotId(botId: string) {
  const { data, error } = await supabase
    .from('backtest_results')
    .select(`*, bots (id, name, strategy, exchange, quote_currency)`)
    .eq('bot_id', botId)
    .is('deleted_at', null)
    .order('created_at', { ascending: false });

  if (error) return [];
  return data || [];
}

export async function fetchBacktestDetails(id: string) {
  const { data, error } = await supabase
    .from('backtest_results')
    .select(`
      *,
      bots (id, name, strategy, strategy_key, exchange, quote_currency, selected_coins, primary_time_interval, secondary_time_enabled, secondary_time_interval, account_balance)
    `)
    .eq('id', id)
    .maybeSingle();

  if (error) throw error;
  if (!data) throw new Error('Backtest not found');

  return { row: data, from: 'backtest_results' };
}

// 🔥 400 KLAIDA IŠTAISYTA ČIA: NAUDOJAMI TEISINGI DUOMENŲ BAZĖS STULPELIAI!
export async function fetchBacktestStats(id: string) {
  const { data, error } = await supabase
    .from('backtest_results')
    .select('id,total_trades,winning_trades,losing_trades,win_rate_pct,net_profit_amount,fees_total_usd')
    .eq('id', id)
    .maybeSingle();

  if (error || !data) return null;

  const winRate = Number(data.win_rate_pct) || 0;
  const totalTrades = Number(data.total_trades) || 0;
  const winningTrades = Number(data.winning_trades) || Math.round(totalTrades * winRate / 100);

  return {
    id: data.id,
    total_trades: totalTrades,
    orders_buy: Math.ceil(totalTrades / 2),
    orders_sell: Math.floor(totalTrades / 2),
    sell_wins: winningTrades,
    sell_losses: totalTrades - winningTrades,
    win_rate_pct: winRate,
    avg_win_pct: null,
    avg_loss_pct: null,
    profit_factor: null,
    best_trade_pct: null,
    worst_trade_pct: null,
    gross_profit_usd: Number(data.net_profit_amount) || 0,
    gross_loss_usd: Number(data.fees_total_usd) || 0
  };
}

export async function fetchBacktestCardRow(id: string) {
  const { data, error } = await supabase
    .from('v_backtests_cards_v3')
    .select('*')
    .eq('id', id)
    .maybeSingle();

  if (error || !data) return null;
  return data;
}

export async function deleteBacktest(backtestId: string): Promise<boolean> {
  const { data, error } = await supabase
    .from('backtest_results')
    .update({ deleted_at: new Date().toISOString(), status: 'deleted' })
    .eq('id', backtestId)
    .select('id, deleted_at, status');

  if (error || !data || data.length === 0) return false;
  return true;
}

export async function hardDeleteBacktest(userId: string, backtestId: string) {
  const { data, error } = await supabase.rpc('f_backtest_hard_delete', {
    p_user_id: userId,
    p_backtest_id: backtestId
  });

  if (error) throw error;
  return { success: true, alreadyDeleted: !data };
}

export async function activateBotFromBacktest(
  userId: string,
  backtestId: string,
  target: 'live' | 'paper' = 'live'
) {
  const { data, error } = await supabase.rpc('f_activate_bot_from_backtest', {
    p_backtest_id: backtestId,
    p_user_id: userId,
    p_target: target
  });

  if (error) throw new Error(error.message || 'Activate failed');
  const res = Array.isArray(data) ? data[0] : data;
  if (!res?.bot_id) throw new Error('RPC returned no bot_id');

  return { bot_id: res.bot_id, created: res.created, name: res.name, target: res.target };
}